import ExcelJS from "exceljs";
import { DashboardData, DashboardFilters } from "./types";

export type DashboardExportFormat = "csv" | "pdf" | "json" | "xlsx";

export interface DashboardExportPayload {
  filters: DashboardFilters;
  dataPeriod: DashboardData["dataPeriod"];
  refreshedAt: string | null;
  overview: DashboardData["overview"];
  aiProcessing: DashboardData["aiProcessing"];
  reviewActivity: DashboardData["reviewActivity"];
  qualitySampling: DashboardData["qualitySampling"];
  correctiveActions: DashboardData["correctiveActions"];
}

export interface DashboardExportRow {
  section: string;
  metric: string;
  value: string | number;
}

const screeningFamilyLabels: Record<string, string> = {
  ALL: "All risk areas and queues",
  PV_PTQ: "PV + PTQ",
  PCO: "PCO",
};

const percentage = (value: number | null) =>
  value === null ? "Not available" : `${value.toFixed(1)}%`;

export const buildDashboardExportPayload = ({
  data,
  filters,
}: {
  data: DashboardData;
  filters: DashboardFilters;
}): DashboardExportPayload => ({
  filters,
  dataPeriod: data.dataPeriod,
  refreshedAt: data.refreshedAt,
  overview: data.overview,
  aiProcessing: data.aiProcessing,
  reviewActivity: data.reviewActivity,
  qualitySampling: data.qualitySampling,
  correctiveActions: data.correctiveActions,
});

export const buildDashboardExportRows = ({
  payload,
}: {
  payload: DashboardExportPayload;
}): DashboardExportRow[] => {
  const processingRate = payload.aiProcessing.recordsReceived
    ? (payload.aiProcessing.recordsProcessed /
        payload.aiProcessing.recordsReceived) *
      100
    : null;
  const aiFlaggedRate = payload.aiProcessing.recordsProcessed
    ? (payload.aiProcessing.aiFlaggedRecords /
        payload.aiProcessing.recordsProcessed) *
      100
    : null;

  return [
    {
      section: "Filters",
      metric: "Date range",
      value: `${payload.dataPeriod.startDate} to ${payload.dataPeriod.endDate}`,
    },
    { section: "Filters", metric: "Region", value: payload.filters.region },
    { section: "Filters", metric: "Country", value: payload.filters.country },
    {
      section: "Filters",
      metric: "Risk area or screening queue",
      value:
        screeningFamilyLabels[payload.filters.screeningFamily] ??
        payload.filters.screeningFamily,
    },
    {
      section: "Filters",
      metric: "Last refreshed",
      value: payload.refreshedAt ?? "Not available",
    },
    {
      section: "Overview",
      metric: "Records received",
      value: payload.overview.recordsReceived,
    },
    {
      section: "Overview",
      metric: "AI-flagged records",
      value: payload.overview.aiFlaggedRecords,
    },
    {
      section: "Overview",
      metric: "Completed reviews",
      value: payload.overview.completedReviews,
    },
    {
      section: "Overview",
      metric: "Sample pass rate",
      value: percentage(payload.overview.samplePassRate),
    },
    {
      section: "AI Processing",
      metric: "Records received",
      value: payload.aiProcessing.recordsReceived,
    },
    {
      section: "AI Processing",
      metric: "Successfully processed",
      value: payload.aiProcessing.recordsProcessed,
    },
    {
      section: "AI Processing",
      metric: "Processing rate",
      value: percentage(processingRate),
    },
    {
      section: "AI Processing",
      metric: "AI-flagged records",
      value: payload.aiProcessing.aiFlaggedRecords,
    },
    {
      section: "AI Processing",
      metric: "AI-flagged rate",
      value: percentage(aiFlaggedRate),
    },
    {
      section: "AI Processing",
      metric: "Awaiting processing",
      value: payload.aiProcessing.recordsAwaitingProcessing,
    },
    {
      section: "AI Processing",
      metric: "Records not flagged",
      value: payload.aiProcessing.recordsNotFlagged,
    },
    {
      section: "AI Processing",
      metric: "QC Pass",
      value: payload.aiProcessing.flaggedRecordsQcPass,
    },
    {
      section: "AI Processing",
      metric: "QC Fail",
      value: payload.aiProcessing.flaggedRecordsQcFail,
    },
    {
      section: "AI Processing",
      metric: "Awaiting review",
      value: payload.aiProcessing.flaggedRecordsAwaitingReview,
    },
    {
      section: "Quality & Sampling",
      metric: "Completed samples",
      value: payload.qualitySampling.completedSamples,
    },
    {
      section: "Quality & Sampling",
      metric: "Unevaluated samples",
      value: payload.qualitySampling.unevaluatedSamples,
    },
    {
      section: "Quality & Sampling",
      metric: "Countries represented",
      value: payload.qualitySampling.countriesRepresented,
    },
    {
      section: "Quality & Sampling",
      metric: "Sample pass rate",
      value: percentage(payload.qualitySampling.samplePassRate),
    },
    ...payload.qualitySampling.completedSamplesByCountry.map((country) => ({
      section: "Completed samples by country",
      metric: country.country,
      value: country.completedSamples,
    })),
    {
      section: "Review Activity",
      metric: "Completed reviews",
      value: payload.reviewActivity.completedReviews,
    },
    {
      section: "Review Activity",
      metric: "Countries with reviews",
      value: payload.reviewActivity.countriesWithReviews,
    },
    ...payload.reviewActivity.reviewsByCountry.map((country) => ({
      section: "Reviews by country",
      metric: country.country,
      value: country.completedReviews,
    })),
    ...payload.reviewActivity.completedReviewsByWeek.flatMap((week) => [
      {
        section: "Completed reviews by risk type",
        metric: `${week.weekStart} PV + PTQ`,
        value: week.pvPtq,
      },
      {
        section: "Completed reviews by risk type",
        metric: `${week.weekStart} PCO`,
        value: week.pco,
      },
    ]),
    ...payload.reviewActivity.qcFailRateByWeek.flatMap((week) => [
      {
        section: "QC Fail rate by week",
        metric: `${week.weekStart} PV + PTQ`,
        value: percentage(week.pvPtq),
      },
      {
        section: "QC Fail rate by week",
        metric: `${week.weekStart} PCO`,
        value: percentage(week.pco),
      },
    ]),
    {
      section: "Corrective Actions",
      metric: "QC Fail evaluations",
      value: payload.correctiveActions.qcFailEvaluations,
    },
    {
      section: "Corrective Actions",
      metric: "Notifications sent",
      value: payload.correctiveActions.notificationsSent,
    },
    {
      section: "Corrective Actions",
      metric: "Acknowledged",
      value: payload.correctiveActions.acknowledged,
    },
    ...payload.correctiveActions.qcFailEvaluationsByScreening.map(
      (screening) => ({
        section: "QC Fail evaluations by screening",
        metric:
          screeningFamilyLabels[screening.screeningFamily] ??
          screening.screeningFamily,
        value: screening.evaluations,
      }),
    ),
  ];
};

const escapeCsv = (value: string | number) => {
  const text = String(value);
  const safeText =
    typeof value === "string" && /^[=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\n\r]/.test(safeText)
    ? `"${safeText.replace(/"/g, '""')}"`
    : safeText;
};

export const dashboardRowsToCsv = ({ rows }: { rows: DashboardExportRow[] }) =>
  [
    ["Section", "Metric", "Value"],
    ...rows.map((row) => [row.section, row.metric, row.value]),
  ]
    .map((row) => row.map(escapeCsv).join(","))
    .join("\r\n");

const worksheetNameOverrides: Record<string, string> = {
  "QC Fail evaluations by screening": "QC Fail evals by screening",
};

const worksheetNameFor = (section: string) =>
  worksheetNameOverrides[section] ?? section.slice(0, 31);

const excelSafeValue = (value: string | number) =>
  typeof value === "string" && /^[=+\-@]/.test(value) ? `'${value}` : value;

export const buildDashboardWorkbook = ({
  rows,
}: {
  rows: DashboardExportRow[];
}) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Text Sentinel";

  Array.from(new Set(rows.map((row) => row.section))).forEach((section) => {
    const worksheet = workbook.addWorksheet(worksheetNameFor(section));
    worksheet.columns = [
      { header: "Metric", key: "metric", width: 42 },
      { header: "Value", key: "value", width: 24 },
    ];
    worksheet.views = [{ state: "frozen", ySplit: 1 }];
    worksheet.autoFilter = "A1:B1";

    rows
      .filter((row) => row.section === section)
      .forEach((row) =>
        worksheet.addRow({
          metric: excelSafeValue(row.metric),
          value: excelSafeValue(row.value),
        }),
      );

    const header = worksheet.getRow(1);
    header.font = { bold: true, color: { argb: "FFFFFFFF" } };
    header.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF1C359A" },
    };
  });

  return workbook;
};

const downloadBlob = ({ blob, fileName }: { blob: Blob; fileName: string }) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const downloadDashboard = async ({
  format,
  data,
  filters,
}: {
  format: DashboardExportFormat;
  data: DashboardData;
  filters: DashboardFilters;
}) => {
  if (format === "pdf") {
    window.print();
    return;
  }

  const payload = buildDashboardExportPayload({ data, filters });
  const rows = buildDashboardExportRows({ payload });
  const baseName = `text-sentinel-dashboard-${payload.dataPeriod.endDate}`;

  if (format === "csv") {
    downloadBlob({
      blob: new Blob([dashboardRowsToCsv({ rows })], {
        type: "text/csv;charset=utf-8",
      }),
      fileName: `${baseName}.csv`,
    });
    return;
  }

  if (format === "xlsx") {
    const workbook = buildDashboardWorkbook({ rows });
    const buffer = await workbook.xlsx.writeBuffer();
    downloadBlob({
      blob: new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      fileName: `${baseName}.xlsx`,
    });
    return;
  }

  downloadBlob({
    blob: new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    }),
    fileName: `${baseName}.json`,
  });
};
